import { FC, KeyboardEvent, useCallback, useLayoutEffect, useRef } from 'react'

import PhoneInput from 'components/ui/phone-input'
import AnimatedIconValid from 'components/ui/animated-icon-valid'
import IconCross from 'images/icon-cross.svg'

import cn from 'classnames'

import styles from './styles.module.scss'
import { useTranslation } from 'utils/use-translation'

interface Props {
  value: string
  onChange: (value) => void
  isValid: boolean
  errorMessage?: string
  readonly?: boolean
  isVisibleError?: boolean
  toHideErrorMessage?: boolean
  onEnter?: () => void
  onKeyDown?: (key: KeyboardEvent<HTMLInputElement>) => void
  className?: string
  wrapperClassName?: string
  showSuccess?: boolean
}

const InputPhone: FC<Props> = ({
  isValid,
  value,
  onChange,
  onEnter,
  isVisibleError = true,
  toHideErrorMessage = false,
  errorMessage = null,
  readonly = false,
  onKeyDown,
  className,
  wrapperClassName,
  showSuccess = true,
}) => {
  const { t } = useTranslation()
  
  const inputRef = useRef(null)
  const isError = !isValid && isVisibleError

  const handleChange = useCallback(
    (value?: string) => {
      const _ = value && value[0] === '+' ? value.slice(1) : value || ''
      onChange(_)
    },
    [onChange],
  )

  useLayoutEffect(() => {
    const onInputEnter = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && onEnter) {
        onEnter()
      }
    }

    if (typeof window !== 'undefined' && inputRef.current) {
      inputRef.current.addEventListener('keydown', onInputEnter)
    }

    return () => {
      if (typeof window !== 'undefined' && inputRef.current) {
        inputRef.current.removeEventListener('keydown', onInputEnter)
      }
    }
  }, [])

  return (
    <div className={cn(styles.phoneWrapper, wrapperClassName)}>
      <PhoneInput
        value={value ? `+${value}` : undefined}
        onChange={handleChange}
        placeholder="+1 000 000 000"
        readOnly={readonly}
        disabled={readonly}
        ref={inputRef}
        defaultCountry="US"
        international
        className={cn(styles.input, className)}
        onKeyDown={onKeyDown}
      />

      {isError && (
        <img
          className={cn(styles.crossIcon)}
          src={IconCross}
          alt="invalid"
          height={14}
          width={14}
        />
      )}

      {!toHideErrorMessage && (isError || errorMessage) && (
        <span className={cn(styles.incorrectPhone)}>
          {errorMessage || t('incorrect_phone')}
        </span>
      )}
      {isValid && showSuccess && <AnimatedIconValid />}
    </div>
  )
}

export default InputPhone
